/**
 * WorkspaceDocumentsPanel — document sidebar used inside ProviderWorkspace.
 *
 * Shows uploaded docs with download buttons, pending-upload slots for any
 * missing required docs, and a general "add more" upload trigger.
 */
import * as React from 'react';
import { Archive, FileText, Upload, Download, Loader2, Plus } from 'lucide-react';
import { Input, toast } from '@finmin/ui';
import { getDocLabel } from '../utils/format-utils';
import { validateDocumentFile } from '../utils/file-utils';
import { ACCEPT_ATTR } from '../constants';
import { PALETTE_FOREST, PALETTE_MINT } from '../tokens';

interface Doc {
  id:              string;
  document_type:   string;
  document_label?: string | null;
  is_downloadable: boolean;
}

/** A free-form "add more" file staged locally until the customer names and uploads it. */
interface PendingFile {
  id:    string;
  file:  File;
  label: string;
}

interface WorkspaceDocumentsPanelProps {
  uploadedDocs:     Doc[];
  missingDocTypes:  string[];
  isDownloading:    boolean;
  downloadingDocId: string | undefined;
  uploadingId:      string | null;
  onDownload:       (documentId: string) => void;
  /** Resolves true once the file is uploaded, false on failure (so we can keep the staged row). */
  onUpload:         (file: File, documentType?: string, documentLabel?: string) => Promise<boolean>;
  /** Zip + download every downloadable doc (assembled client-side; no bulk endpoint). */
  onDownloadAll?:   () => void;
  isZipping?:       boolean;
  /** Service Closure: hide every upload control while the service is on hold / closed. */
  readOnly?:        boolean;
}

export function WorkspaceDocumentsPanel({
  uploadedDocs,
  missingDocTypes,
  isDownloading,
  downloadingDocId,
  uploadingId,
  onDownload,
  onUpload,
  onDownloadAll,
  isZipping,
  readOnly = false,
}: WorkspaceDocumentsPanelProps) {
  // Free-form "add more" files wait here until the customer gives each a name.
  const [pending, setPending]                 = React.useState<PendingFile[]>([]);
  const [uploadingPendingId, setUploadingPendingId] = React.useState<string | null>(null);

  function stagePicked(e: React.ChangeEvent<HTMLInputElement>) {
    Array.from(e.target.files ?? []).forEach(f => {
      const err = validateDocumentFile(f);
      if (err) { toast.error(err); return; }
      setPending(prev => [...prev, { id: crypto.randomUUID(), file: f, label: '' }]);
    });
    e.target.value = '';
  }

  async function uploadPending(p: PendingFile) {
    setUploadingPendingId(p.id);
    try {
      const ok = await onUpload(p.file, 'other', p.label.trim());
      if (ok) setPending(prev => prev.filter(x => x.id !== p.id));
    } finally {
      setUploadingPendingId(null);
    }
  }

  return (
    <div className="space-y-4">

      {/* ── Uploaded docs ─────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: PALETTE_FOREST }}>
            Uploaded Docs ({uploadedDocs.length})
          </p>
          {onDownloadAll && uploadedDocs.filter(d => d.is_downloadable).length > 1 && (
            <button
              type="button"
              onClick={onDownloadAll}
              disabled={isZipping}
              className="inline-flex items-center gap-1 text-[10px] font-semibold transition-opacity hover:opacity-70 disabled:opacity-50"
              style={{ color: PALETTE_FOREST }}
              title="Download all documents as a ZIP"
            >
              {isZipping ? <Loader2 className="h-3 w-3 animate-spin" /> : <Archive className="h-3 w-3" />}
              {isZipping ? 'Zipping…' : 'Download all'}
            </button>
          )}
        </div>
        {uploadedDocs.length === 0 ? (
          <p className="text-xs text-muted-foreground italic text-center py-3 border border-dashed border-border rounded-xl">
            No documents uploaded yet
          </p>
        ) : (
          <div className="space-y-2">
            {uploadedDocs.map(doc => {
              const isThisDownloading = isDownloading && downloadingDocId === doc.id;
              return (
                <div
                  key={doc.id}
                  className="flex items-center gap-2.5 rounded-xl border border-border bg-background p-2.5"
                >
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: PALETTE_MINT }}
                  >
                    <FileText className="h-3.5 w-3.5" style={{ color: PALETTE_FOREST }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">
                      {doc.document_label ?? getDocLabel(doc.document_type)}
                    </p>
                    <p className="text-[10px] text-muted-foreground capitalize">
                      {doc.document_type.replace(/_/g, ' ')}
                    </p>
                  </div>
                  {doc.is_downloadable && (
                    <button
                      type="button"
                      onClick={() => onDownload(doc.id)}
                      disabled={isDownloading}
                      className="shrink-0 h-7 w-7 rounded-lg flex items-center justify-center transition-colors hover:opacity-70 disabled:opacity-40"
                      style={{ backgroundColor: `${PALETTE_FOREST}12`, color: PALETTE_FOREST }}
                      title="Download"
                      aria-label={`Download ${doc.document_label ?? getDocLabel(doc.document_type)}`}
                    >
                      {isThisDownloading
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Download className="h-3.5 w-3.5" />
                      }
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Pending uploads ───────────────────────────────────────── */}
      {!readOnly && missingDocTypes.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-600 mb-2">
            Pending ({missingDocTypes.length})
          </p>
          <div className="space-y-2">
            {missingDocTypes.map(dt => (
              <label
                key={dt}
                className="flex items-center justify-between rounded-xl border border-dashed border-border px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <input
                  type="file"
                  accept={ACCEPT_ATTR}
                  className="sr-only"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) onUpload(f, dt);
                    e.target.value = '';
                  }}
                />
                <span className="text-xs text-muted-foreground truncate">{getDocLabel(dt)}</span>
                <span
                  className="text-[10px] font-semibold flex items-center gap-1 shrink-0 ml-2"
                  style={{ color: PALETTE_FOREST }}
                >
                  <Upload className="h-3 w-3" /> Upload
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* ── Add more ──────────────────────────────────────────────── */}
      {readOnly ? (
        <p className="text-[11px] text-muted-foreground">
          Uploads are paused while this service is on hold or closed.
        </p>
      ) : (
      <div className="space-y-2">
        {/* Staged files awaiting a name before upload. */}
        {pending.map(p => {
          const invalid   = !p.label.trim();
          const uploading = uploadingPendingId === p.id;
          return (
            <div key={p.id} className="rounded-xl border border-border bg-background p-2.5 space-y-2">
              <div className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-foreground truncate flex-1">{p.file.name}</span>
                {!uploading && (
                  <button
                    type="button"
                    onClick={() => setPending(prev => prev.filter(x => x.id !== p.id))}
                    className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                    aria-label={`Remove ${p.file.name}`}
                  >
                    ×
                  </button>
                )}
              </div>
              <Input
                value={p.label}
                onChange={e => setPending(prev => prev.map(x => x.id === p.id ? { ...x, label: e.target.value } : x))}
                placeholder="Name this document (e.g. Rent agreement)"
                aria-label={`Name for ${p.file.name}`}
                aria-invalid={invalid}
                disabled={uploading}
                className={`h-8 text-xs ${invalid ? 'border-destructive focus-visible:ring-destructive' : ''}`}
              />
              <button
                type="button"
                disabled={invalid || uploading}
                onClick={() => uploadPending(p)}
                className="w-full h-8 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1 transition-colors disabled:opacity-50"
                style={{ backgroundColor: PALETTE_MINT, color: PALETTE_FOREST }}
              >
                {uploading
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</>
                  : <><Upload className="h-3 w-3" /> Upload</>
                }
              </button>
            </div>
          );
        })}

        <label className="flex items-center gap-2 rounded-xl border border-dashed border-border px-3 py-3 cursor-pointer hover:bg-muted/50 transition-colors">
          <input
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            className="sr-only"
            onChange={stagePicked}
          />
          <div
            className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: PALETTE_MINT }}
          >
            {uploadingId
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: PALETTE_FOREST }} />
              : <Plus className="h-3.5 w-3.5" style={{ color: PALETTE_FOREST }} />
            }
          </div>
          <div>
            <p className="text-xs font-medium text-foreground">Add more documents</p>
            <p className="text-[10px] text-muted-foreground">PDF, DOCX, XLSX, JPG, PNG — max 20 MB · name required</p>
          </div>
        </label>
      </div>
      )}
    </div>
  );
}
